import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Investigate Mode state. When `active` is set, the dashboard enters a focused
 * drill-down: the normal pins/towers hide (kept in `alertsSlice`, NOT refetched),
 * both sidebars collapse, a red "INVESTIGATE MODE" border frames the map, and the
 * investigation panel opens on the right. Closing clears `active` and everything
 * restores instantly from Redux — zero network calls.
 *
 * `active` carries only what the request needs; the fetched `InvestigationResult`
 * is held by RTK Query (the mutation cache), not here.
 */
export interface InvestigationTarget {
  /** The investigated anomaly's id — cache key so reopening the same one doesn't refire. */
  anomalyId: string;
  ruleId: string;
  /** The anomaly's site/session key (Anomaly.towerId). */
  towerId: string;
  /** ISO window that produced the anomaly. */
  from: string;
  to: string;
  /** Rule params that produced the anomaly (e.g. cell-location's radius). */
  params?: Record<string, number | string>;
}

interface InvestigationState {
  active: InvestigationTarget | null;
}

const initialState: InvestigationState = { active: null };

const investigationSlice = createSlice({
  name: 'investigation',
  initialState,
  reducers: {
    openInvestigation(state, action: PayloadAction<InvestigationTarget>) {
      state.active = action.payload;
    },
    closeInvestigation(state) {
      state.active = null;
    },
  },
});

export const { openInvestigation, closeInvestigation } = investigationSlice.actions;
export default investigationSlice.reducer;
